import type {
  PromotionBootstrapPayload,
  PromotionDeltaPayload,
  PromotionRuntimeSnapshot,
} from "../gateway/promotion-runtime-client";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

const promotionDomain = "PROMOTION_RUNTIME";

export type LocalPromotionRuntime = {
  initialized: boolean;
  revision: number;
  promotions: PromotionRuntimeSnapshot[];
};

function promotionScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:channel:${identity.commerce_channel_id}:promotion`;
}

function promotionCursorKey(identity: TrustedTerminalIdentity) {
  return `promotion:cursor:${promotionScope(identity)}`;
}

function promotionProjectionKey(identity: TrustedTerminalIdentity, promotionVersionId: string) {
  return `promotion:${promotionScope(identity)}:version:${promotionVersionId}`;
}

function readRevision(value: unknown) {
  if (!value || typeof value !== "object") return 0;
  const revision = (value as { revision?: unknown }).revision;
  return typeof revision === "number" && Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
}

export async function readPromotionRuntime(identity: TrustedTerminalIdentity): Promise<LocalPromotionRuntime> {
  const scopeKey = promotionScope(identity);
  const [meta, rows] = await Promise.all([
    posRuntimeDb.runtimeMeta.get(promotionCursorKey(identity)),
    posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray(),
  ]);

  const promotions = rows
    .filter((row) => row.domain === promotionDomain && row.payload && typeof row.payload === "object")
    .map((row) => row.payload as PromotionRuntimeSnapshot)
    .filter((snapshot) => Boolean(snapshot.promotion_version_id && snapshot.policy_id))
    .sort((a, b) => (a.priority_rank - b.priority_rank) || a.promotion_version_id.localeCompare(b.promotion_version_id));

  return { initialized: Boolean(meta), revision: readRevision(meta?.value), promotions };
}

export async function applyPromotionBootstrapPayload(
  identity: TrustedTerminalIdentity,
  payload: PromotionBootstrapPayload,
) {
  const scopeKey = promotionScope(identity);
  const now = new Date().toISOString();

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    const existing = await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray();
    const keys = existing.filter((row) => row.domain === promotionDomain).map((row) => row.key);
    if (keys.length) await posRuntimeDb.projections.bulkDelete(keys);

    for (const snapshot of payload.promotions ?? []) {
      await posRuntimeDb.projections.put({
        key: promotionProjectionKey(identity, snapshot.promotion_version_id),
        scopeKey,
        domain: promotionDomain,
        revision: String(payload.revision),
        payload: snapshot,
        updatedAt: now,
      });
    }

    await posRuntimeDb.runtimeMeta.put({
      key: promotionCursorKey(identity),
      scopeKey,
      value: { initialized: true, revision: payload.revision },
      updatedAt: now,
    });
  });

  return payload.revision;
}

export async function applyPromotionDeltaPayload(
  identity: TrustedTerminalIdentity,
  payload: PromotionDeltaPayload,
) {
  const scopeKey = promotionScope(identity);
  const now = new Date().toISOString();

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    for (const event of payload.events ?? []) {
      const key = promotionProjectionKey(identity, event.promotion_version_id);
      if (event.event_type === "TOMBSTONE") {
        await posRuntimeDb.projections.delete(key);
      } else {
        await posRuntimeDb.projections.put({
          key,
          scopeKey,
          domain: promotionDomain,
          revision: String(event.revision),
          payload: event.runtime_snapshot,
          updatedAt: now,
        });
      }
    }

    await posRuntimeDb.runtimeMeta.put({
      key: promotionCursorKey(identity),
      scopeKey,
      value: { initialized: true, revision: payload.next_revision },
      updatedAt: now,
    });
  });

  return payload.next_revision;
}
